import { FormControl, MenuItem, Select, SelectChangeEvent, Stack } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LOCALES, type Locale } from "verdi-common/i18n";
import { Tag } from "@/components/tag/tag";
import { getInitialLocale, isSupportedLocale, setStoredLocale } from "@/i18n/locale";

// Per-locale UI metadata for the dropdown. `code` must reference a locale in
// `verdi-common`'s `LOCALES`, which is the single source of truth for supported
// languages. The module-load assertion below fails the build if these two ever drift.
const languageOptions: Array<{ code: Locale; flagCode: string; label: string; beta: boolean }> = [
    { code: "en-CA", flagCode: "CA", label: "English", beta: false },
    { code: "en-US", flagCode: "US", label: "English", beta: false },
    { code: "es-MX", flagCode: "MX", label: "Español", beta: true },
    { code: "pa-IN", flagCode: "IN", label: "ਪੰਜਾਬੀ", beta: true },
    { code: "fr-CA", flagCode: "CA", label: "Français", beta: true },
    { code: "pt-PT", flagCode: "PT", label: "Português", beta: true },
    { code: "ja-JP", flagCode: "JP", label: "日本語", beta: true },
];

// Module-load assertion that every supported locale has a corresponding dropdown option.
// Runs once when this module is evaluated — cheap and fail-loud, so adding a locale to
// verdi-common's `LOCALES` without a dashboard UI option for it produces an immediate
// clear error instead of a missing-option silent default.
const _coveredCodes = new Set(languageOptions.map((opt) => opt.code));
const _missingLocales = LOCALES.filter((locale) => !_coveredCodes.has(locale));
if (_missingLocales.length > 0) {
    throw new Error(
        `LanguageSelect is missing UI options for locales: [${_missingLocales.join(", ")}]. ` +
            `Add an entry to languageOptions or remove the locale from verdi-common's LOCALES list.`,
    );
}

function flagUrl(flagCode: string): string {
    return `https://purecatamphetamine.github.io/country-flag-icons/3x2/${flagCode}.svg`;
}

/**
 * Flag + native-language label for a locale, matching the app language picker's visual treatment —
 * the flag is what tells the two Englishes apart.
 */
export function LocaleDisplay({ locale, flagSize = 12 }: { locale: Locale; flagSize?: number }): JSX.Element {
    const option = languageOptions.find((candidate) => candidate.code === locale);
    if (!option) {
        return <span>{locale}</span>;
    }
    return (
        <span className={"inline-flex items-center gap-1.5"}>
            <img loading={"lazy"} alt={""} src={flagUrl(option.flagCode)} style={{ height: flagSize }} />
            {option.label}
        </span>
    );
}

interface LanguageSelectProps {
    flagSize?: number;
    includeLabel?: boolean;
}

/**
 * A dropdown component that allows the user to select a language for the app.
 * @param flagSize - The size of the flag icons in the dropdown.
 * @param includeLabel - Whether to include the language label in the dropdown.
 * @returns A dropdown component that allows the user to select a language for the app.
 *
 * First load (no stored preference): Stored → browser (exact or mapped) → default; that result drives both the dropdown and i18n.
 * First load (with stored preference): Stored value wins; dropdown and i18n use it.
 * User selects a language: Selection is saved to localStorage and state; next time getInitialLocale() runs (e.g. on reload), it returns that stored value.
 */
export function LanguageSelect({ flagSize, includeLabel = false }: LanguageSelectProps) {
    const { i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const [language, setLanguage] = useState<Locale>(getInitialLocale);

    const onChangeLanguageOption = (e: SelectChangeEvent) => {
        const selectedLanguage = e.target.value;

        // Ensure selected language is valid before attempting to set
        if (!isSupportedLocale(selectedLanguage)) {
            return;
        }

        setLanguage(selectedLanguage);
        // Update i18n immediately
        i18n.changeLanguage(selectedLanguage);
        setStoredLocale(selectedLanguage);
    };

    return (
        <Stack justifyContent={"center"}>
            <FormControl size={"small"}>
                <Select
                    size={"small"}
                    open={open}
                    onOpen={() => setOpen(!open)}
                    value={language}
                    onChange={onChangeLanguageOption}
                    onClose={() => setOpen(false)}
                    MenuProps={{
                        sx: {
                            top: 5, // Aligns the dropdown with the input
                        },
                    }}
                    IconComponent={() => null} // Remove the arrow icon
                    inputProps={{
                        sx: {
                            pr: "12px !important", // reclaim the arrow's spacing on the right
                        },
                    }}
                    sx={{
                        ".MuiOutlinedInput-notchedOutline": { border: 0 },
                        // Override focus state
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderWidth: "1px",
                        },
                        // Fix flag height using !important, since all specific classes get overridden by other Mui classes on render
                        ".MuiOutlinedInput-input": {
                            minHeight: "unset !important",
                        },
                    }}
                    renderValue={(selected) => {
                        const selectedOption =
                            languageOptions.find((option) => option.code === selected) || languageOptions[0];
                        return (
                            <LanguageOptionItem
                                option={selectedOption}
                                hideBetaTag={true}
                                withLabel={includeLabel}
                                flagSize={flagSize}
                            />
                        );
                    }}
                >
                    {languageOptions.map((option) => (
                        <MenuItem key={option.code} value={option.code} sx={{ display: "flex", alignItems: "center" }}>
                            <LanguageOptionItem option={option} flagSize={flagSize} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Stack>
    );
}

interface LanguageOptionItemProps {
    option: {
        code: string;
        flagCode: string;
        label: string;
        beta: boolean;
    };
    hideBetaTag?: boolean;
    withLabel?: boolean;
    flagSize?: number;
}

function LanguageOptionItem({
    option,
    withLabel = true,
    hideBetaTag = false,
    flagSize = 10,
}: LanguageOptionItemProps): JSX.Element {
    return (
        <Stack direction={"row"} justifyContent={"space-between"} flexGrow={1}>
            <Stack direction={"row"} alignItems={"center"} style={{ marginRight: hideBetaTag ? 0 : 8 }}>
                <img
                    loading={"lazy"}
                    src={flagUrl(option.flagCode)}
                    style={{
                        height: flagSize,
                        marginRight: withLabel ? 6 : 0,
                        filter: "brightness(0.95)",
                    }}
                />
                {withLabel && option.label}
            </Stack>
            {!hideBetaTag && option.beta && <Tag />}
        </Stack>
    );
}
